/* eslint-disable react/forbid-prop-types */
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  FiUpload,
  FiCheckCircle,
  FiLoader,
  FiFile,
  FiTrash2,
  FiInfo,
} from 'react-icons/fi';
import { ESIM_DOCUMENT_FIELDS } from '../../constants/esimConstants';
import { uploadEsimDocumentImageApi } from '../../api/esimApi';
import './EsimDocumentUpload.scss';

const ACCEPTED_FORMATS = '.jpg,.jpeg,.png,.pdf';
const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

function DocSlot({ field, value, onChange, showErrors }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const showRequiredError = showErrors && field.required && !value?.documentUrl;

  const handleChoose = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFile = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (file.size > MAX_SIZE_BYTES) {
      setError('File too large — please upload an image under 5 MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setUploading(true);
    setError('');
    try {
      const url = await uploadEsimDocumentImageApi(file);
      if (!url) {
        setError('Upload failed. Please try again.');
        return;
      }
      const ext = (file.name.split('.').pop() || '').toUpperCase();
      onChange(field.key, {
        documentUrl: url,
        documentFormat: ext || 'JPG',
        fileName: file.name,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('upload failed', err);
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemove = () => {
    onChange(field.key, {
      documentUrl: '',
      documentFormat: '',
      fileName: '',
    });
  };

  return (
    <div className="esim-doc-upload__slot">
      <div className="esim-doc-upload__slot-head">
        <span className="esim-doc-upload__slot-name">
          <FiFile />
          {field.label}
          {field.required && (
            <span className="esim-doc-upload__slot-required">*</span>
          )}
        </span>
        {!field.required && (
          <span className="esim-doc-upload__slot-optional">Optional</span>
        )}
      </div>

      <p className="esim-doc-upload__slot-helper">{field.helper}</p>

      {!value?.documentUrl ? (
        <button
          type="button"
          className={`esim-doc-upload__drop ${
            showRequiredError ? 'esim-doc-upload__drop--error' : ''
          }`}
          onClick={handleChoose}
          disabled={uploading}
        >
          {uploading ? (
            <>
              <FiLoader className="esim-doc-upload__spin" />
              <span>Uploading…</span>
            </>
          ) : (
            <>
              <FiUpload />
              <span>Click to upload (JPG, PNG, PDF — max 5 MB)</span>
            </>
          )}
        </button>
      ) : (
        <div className="esim-doc-upload__preview">
          <FiCheckCircle className="esim-doc-upload__preview-ok" />
          <span className="esim-doc-upload__preview-name">
            {value.fileName || 'Uploaded file'}
          </span>
          <button
            type="button"
            className="esim-doc-upload__preview-remove"
            onClick={handleRemove}
            aria-label="Remove file"
          >
            <FiTrash2 />
          </button>
        </div>
      )}

      {showRequiredError && (
        <small className="esim-doc-upload__err">
          Please upload {field.label}.
        </small>
      )}
      {error && <small className="esim-doc-upload__err">{error}</small>}

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_FORMATS}
        style={{ display: 'none' }}
        onChange={handleFile}
      />
    </div>
  );
}

DocSlot.propTypes = {
  field: PropTypes.object.isRequired,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  showErrors: PropTypes.bool.isRequired,
};

DocSlot.defaultProps = { value: null };

function EsimDocumentUpload({ docs, onChange, showErrors }) {
  const handleSlotChange = (key, next) => {
    onChange({ ...docs, [key]: next });
  };

  return (
    <section className="esim-doc-upload">
      <header className="esim-doc-upload__head">
        <h3 className="esim-doc-upload__title">Upload Documents</h3>
        <p className="esim-doc-upload__sub">
          <FiInfo /> Two passport pages are mandatory. Add e-tickets and visa
          copies if you have them — they speed up activation.
        </p>
      </header>

      <div className="esim-doc-upload__grid">
        {ESIM_DOCUMENT_FIELDS.map((field) => (
          <DocSlot
            key={field.key}
            field={field}
            value={docs?.[field.key]}
            onChange={handleSlotChange}
            showErrors={showErrors}
          />
        ))}
      </div>
    </section>
  );
}

EsimDocumentUpload.propTypes = {
  docs: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  showErrors: PropTypes.bool,
};

EsimDocumentUpload.defaultProps = {
  docs: {},
  showErrors: false,
};

export default EsimDocumentUpload;
